import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import {
  Cloud, Database, Webhook, Loader2, RefreshCw, Search, Copy, Check, Trash2,
  ChevronLeft, ChevronRight, Eye, Users, ShieldAlert, HeartPulse, FileText, RotateCw, Sparkles,
  Send, ExternalLink, Share2, Save, Plus, Cable, Plug, DownloadCloud,
} from "lucide-react";

const TIPO_CONFIG = {
  renovaciones: { label: "Renovaciones", icon: RotateCw, color: "bg-blue-50 text-blue-700 border-blue-200" },
  reclamos_salud: { label: "Reclamos Salud", icon: HeartPulse, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  reclamos_riesgos: { label: "Reclamos Riesgos", icon: ShieldAlert, color: "bg-amber-50 text-amber-700 border-amber-200" },
  clientes: { label: "Clientes", icon: Users, color: "bg-violet-50 text-violet-700 border-violet-200" },
  polizas: { label: "Pólizas", icon: FileText, color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  cotizaciones: { label: "Cotizaciones", icon: FileText, color: "bg-pink-50 text-pink-700 border-pink-200" },
  otro: { label: "Otro", icon: Database, color: "bg-slate-50 text-slate-700 border-slate-200" },
};
const INGESTABLES = ["renovaciones", "polizas", "reclamos_salud", "reclamos_riesgos"];
const CAMPOS = ["external_id", "cliente", "titulo", "estado", "monto", "fecha_dato", "poliza"];
const CAMPO_LABEL = { external_id: "ID externo", cliente: "Cliente", titulo: "Título", estado: "Estado", monto: "Monto", fecha_dato: "Fecha", poliza: "Póliza" };

function StatCard({ label, value, icon: Icon, color, active, onClick }) {
  return (
    <Card data-testid={`crm-stat-${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={onClick}
      className={`border-0 shadow-sm cursor-pointer transition-all hover:shadow-md ${active ? "ring-2 ring-blue-500" : ""}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
        <div><p className="text-2xl font-bold leading-none">{value}</p><p className="text-xs text-muted-foreground mt-1">{label}</p></div>
      </CardContent>
    </Card>
  );
}

const emptyForm = () => ({
  nombre: "", descripcion: "", activo: true, modo_api_habilitado: false,
  ruteo: { auto_distribuir: false, reclamos_salud: { pipeline_id: null, stage_id: null }, reclamos_riesgos: { pipeline_id: null, stage_id: null } },
  mapeos: {},
  api: { base_url: "", auth: { auth_type: "none", header_name: "Authorization", api_key: "", token: "", username: "", password: "" }, recursos: [] },
});

// ---------- Editor de conexión ----------
function ConexionEditor({ open, onClose, conexionId, opciones, getAuthHeaders, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [webhookUrl, setWebhookUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const isEdit = !!conexionId;

  useEffect(() => {
    if (!open) return;
    if (!conexionId) { setForm(emptyForm()); setWebhookUrl(""); return; }
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/crm/conexiones/${conexionId}`, getAuthHeaders());
        const d = res.data;
        setForm({
          nombre: d.nombre || "", descripcion: d.descripcion || "", activo: !!d.activo,
          modo_api_habilitado: !!d.modo_api_habilitado,
          ruteo: d.ruteo || emptyForm().ruteo, mapeos: d.mapeos || {},
          api: { ...emptyForm().api, ...(d.api || {}), auth: { ...emptyForm().api.auth, ...((d.api || {}).auth || {}) } },
        });
        setWebhookUrl(d.webhook_url || "");
      } catch (e) { toast.error("No se pudo cargar la conexión"); }
      finally { setLoading(false); }
    })();
  }, [open, conexionId, getAuthHeaders]);

  const stagesOf = (pid) => (opciones.find((p) => p.id === pid)?.stages) || [];
  const setRuteo = (tipo, field, value) => setForm((f) => {
    const next = { ...f, ruteo: { ...f.ruteo, [tipo]: { ...f.ruteo[tipo], [field]: value } } };
    if (field === "pipeline_id") next.ruteo[tipo].stage_id = null;
    return next;
  });
  const setMapeo = (tipo, campo, value) => setForm((f) => ({ ...f, mapeos: { ...f.mapeos, [tipo]: { ...(f.mapeos[tipo] || {}), [campo]: value } } }));
  const setApi = (patch) => setForm((f) => ({ ...f, api: { ...f.api, ...patch } }));
  const setAuth = (patch) => setForm((f) => ({ ...f, api: { ...f.api, auth: { ...f.api.auth, ...patch } } }));
  const addRecurso = () => setApi({ recursos: [...form.api.recursos, { tipo: "renovaciones", endpoint_path: "", metodo: "GET", params: {}, root_path: "", intervalo_min: 60, activo: true }] });
  const setRecurso = (i, patch) => setApi({ recursos: form.api.recursos.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const delRecurso = (i) => setApi({ recursos: form.api.recursos.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Ponle un nombre a la conexión"); return; }
    try {
      setSaving(true);
      if (isEdit) await axios.put(`${API}/crm/conexiones/${conexionId}`, form, getAuthHeaders());
      else await axios.post(`${API}/crm/conexiones`, form, getAuthHeaders());
      toast.success(isEdit ? "Conexión actualizada" : "Conexión creada");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const sincronizar = async () => {
    try {
      const res = await axios.post(`${API}/crm/conexiones/${conexionId}/sincronizar`, {}, getAuthHeaders());
      toast.success(`Sincronizado: ${res.data.total} registro(s)`);
      if (res.data.errores?.length) toast.warning(res.data.errores[0].error, { duration: 7000 });
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo sincronizar"); }
  };

  const auth = form.api.auth;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="crm-conexion-editor">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar conexión" : "Nueva conexión"}</DialogTitle>
          <DialogDescription>Configura la plataforma externa, su enrutamiento y el mapeo de campos.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div> : (
        <div className="space-y-6">
          {/* Datos básicos */}
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Nombre</Label><Input data-testid="conx-nombre" className="mt-1" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} placeholder="Ej: CRM Salud" /></div>
            <div className="flex items-end gap-2"><Switch data-testid="conx-activo" checked={form.activo} onCheckedChange={(v) => setForm((f) => ({ ...f, activo: v }))} /><span className="text-sm text-slate-600 pb-1">Activa</span></div>
            <div className="md:col-span-2"><Label className="text-xs">Descripción</Label><Textarea className="mt-1" rows={2} value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} /></div>
          </div>

          {isEdit && webhookUrl && (
            <div>
              <Label className="text-xs flex items-center gap-1"><Webhook className="w-3.5 h-3.5" /> URL del Webhook (modo push)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input data-testid="conx-webhook-url" readOnly value={webhookUrl} className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(webhookUrl); setCopied(true); toast.success("Copiado"); setTimeout(() => setCopied(false), 1500); }}>
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}

          {/* Enrutamiento */}
          <div className="space-y-3 border-t pt-4">
            <h4 className="font-semibold text-sm text-slate-800 flex items-center gap-2"><Share2 className="w-4 h-4 text-blue-600" /> Enrutamiento a módulos</h4>
            <div className="flex items-center justify-between p-2 rounded bg-slate-50">
              <span className="text-sm text-slate-600">Distribución automática al recibir</span>
              <Switch data-testid="conx-auto" checked={form.ruteo.auto_distribuir} onCheckedChange={(v) => setForm((f) => ({ ...f, ruteo: { ...f.ruteo, auto_distribuir: v } }))} />
            </div>
            {["reclamos_salud", "reclamos_riesgos"].map((tipo) => (
              <div key={tipo} className="grid md:grid-cols-2 gap-2 items-end">
                <div>
                  <Label className="text-xs">{TIPO_CONFIG[tipo].label} → Pipeline</Label>
                  <Select value={form.ruteo[tipo]?.pipeline_id || ""} onValueChange={(v) => setRuteo(tipo, "pipeline_id", v)}>
                    <SelectTrigger data-testid={`conx-${tipo}-pipeline`} className="mt-1"><SelectValue placeholder="Pipeline" /></SelectTrigger>
                    <SelectContent>{opciones.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Etapa</Label>
                  <Select value={form.ruteo[tipo]?.stage_id || ""} onValueChange={(v) => setRuteo(tipo, "stage_id", v)} disabled={!form.ruteo[tipo]?.pipeline_id}>
                    <SelectTrigger data-testid={`conx-${tipo}-stage`} className="mt-1"><SelectValue placeholder="Etapa" /></SelectTrigger>
                    <SelectContent>{stagesOf(form.ruteo[tipo]?.pipeline_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>

          {/* Mapeo de campos */}
          <div className="space-y-3 border-t pt-4">
            <h4 className="font-semibold text-sm text-slate-800 flex items-center gap-2"><Cable className="w-4 h-4 text-blue-600" /> Mapeo de campos (campo del CRM → QAsesores)</h4>
            <p className="text-xs text-muted-foreground">Escribe el nombre del campo tal como viene en el CRM. Déjalo vacío para usar la detección automática.</p>
            {["renovaciones", "reclamos_salud", "reclamos_riesgos", "clientes", "polizas"].map((tipo) => (
              <details key={tipo} className="rounded border border-slate-200">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700 flex items-center gap-2">{TIPO_CONFIG[tipo].label}</summary>
                <div className="grid md:grid-cols-3 gap-2 p-3 pt-0">
                  {CAMPOS.map((campo) => (
                    <div key={campo}>
                      <Label className="text-[11px] text-muted-foreground">{CAMPO_LABEL[campo]}</Label>
                      <Input data-testid={`map-${tipo}-${campo}`} className="mt-0.5 h-8 text-xs" value={form.mapeos[tipo]?.[campo] || ""} onChange={(e) => setMapeo(tipo, campo, e.target.value)} placeholder="campo_crm" />
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>

          {/* Modo API (pull) */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm text-slate-800 flex items-center gap-2"><DownloadCloud className="w-4 h-4 text-blue-600" /> Modo API — jalar datos (pull)</h4>
              <Switch data-testid="conx-api-enabled" checked={form.modo_api_habilitado} onCheckedChange={(v) => setForm((f) => ({ ...f, modo_api_habilitado: v }))} />
            </div>
            {form.modo_api_habilitado && (
              <div className="space-y-3">
                <div><Label className="text-xs">URL base de la API</Label><Input data-testid="conx-base-url" className="mt-1 text-xs" value={form.api.base_url} onChange={(e) => setApi({ base_url: e.target.value })} placeholder="https://api.tucrm.com/v2" /></div>
                <div className="grid md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Autenticación</Label>
                    <Select value={auth.auth_type} onValueChange={(v) => setAuth({ auth_type: v })}>
                      <SelectTrigger data-testid="conx-auth-type" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin autenticación</SelectItem>
                        <SelectItem value="api_key">API Key (header)</SelectItem>
                        <SelectItem value="bearer">Bearer Token</SelectItem>
                        <SelectItem value="basic">Basic (usuario/clave)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {auth.auth_type === "api_key" && (<>
                    <div><Label className="text-xs">Nombre del header</Label><Input className="mt-1 text-xs" value={auth.header_name || ""} onChange={(e) => setAuth({ header_name: e.target.value })} placeholder="X-API-Key" /></div>
                    <div className="md:col-span-2"><Label className="text-xs">API Key</Label><Input className="mt-1 text-xs" value={auth.api_key || ""} onChange={(e) => setAuth({ api_key: e.target.value })} placeholder="valor de la key" /></div>
                  </>)}
                  {auth.auth_type === "bearer" && (<div className="md:col-span-2"><Label className="text-xs">Token</Label><Input className="mt-1 text-xs" value={auth.token || ""} onChange={(e) => setAuth({ token: e.target.value })} /></div>)}
                  {auth.auth_type === "basic" && (<>
                    <div><Label className="text-xs">Usuario</Label><Input className="mt-1 text-xs" value={auth.username || ""} onChange={(e) => setAuth({ username: e.target.value })} /></div>
                    <div><Label className="text-xs">Contraseña</Label><Input type="password" className="mt-1 text-xs" value={auth.password || ""} onChange={(e) => setAuth({ password: e.target.value })} /></div>
                  </>)}
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-xs">Recursos a consultar (endpoints)</Label>
                  <Button data-testid="conx-add-recurso" type="button" variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={addRecurso}><Plus className="w-3.5 h-3.5" /> Agregar</Button>
                </div>
                {form.api.recursos.map((r, i) => (
                  <div key={i} className="grid md:grid-cols-12 gap-2 items-end p-2 rounded bg-slate-50">
                    <div className="md:col-span-3"><Label className="text-[11px]">Tipo</Label>
                      <Select value={r.tipo} onValueChange={(v) => setRecurso(i, { tipo: v })}>
                        <SelectTrigger className="mt-0.5 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.keys(TIPO_CONFIG).map((t) => <SelectItem key={t} value={t}>{TIPO_CONFIG[t].label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-4"><Label className="text-[11px]">Endpoint</Label><Input className="mt-0.5 h-8 text-xs" value={r.endpoint_path} onChange={(e) => setRecurso(i, { endpoint_path: e.target.value })} placeholder="/renovaciones" /></div>
                    <div className="md:col-span-2"><Label className="text-[11px]">Ruta lista</Label><Input className="mt-0.5 h-8 text-xs" value={r.root_path || ""} onChange={(e) => setRecurso(i, { root_path: e.target.value })} placeholder="data.items" /></div>
                    <div className="md:col-span-2"><Label className="text-[11px]">Cada (min)</Label><Input type="number" className="mt-0.5 h-8 text-xs" value={r.intervalo_min} onChange={(e) => setRecurso(i, { intervalo_min: parseInt(e.target.value) || 60 })} /></div>
                    <div className="md:col-span-1"><Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => delRecurso(i)}><Trash2 className="w-4 h-4" /></Button></div>
                  </div>
                ))}
                {isEdit && (
                  <Button data-testid="conx-sync-now" type="button" variant="outline" size="sm" className="gap-1.5 border-blue-300 bg-blue-50 text-blue-700" onClick={sincronizar}><DownloadCloud className="w-4 h-4" /> Sincronizar ahora</Button>
                )}
                <p className="text-[11px] text-muted-foreground">Nota: el pull se ejecuta contra tu CRM real. Deja esto configurado y actívalo cuando tengas las credenciales del proveedor.</p>
              </div>
            )}
          </div>
        </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="conx-save" onClick={save} disabled={saving} className="gap-1.5">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Tab: Conexiones ----------
function ConexionesTab({ getAuthHeaders, isAdmin }) {
  const [conexiones, setConexiones] = useState([]);
  const [opciones, setOpciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, o] = await Promise.all([
        axios.get(`${API}/crm/conexiones`, getAuthHeaders()),
        axios.get(`${API}/crm/ruteo/opciones`, getAuthHeaders()),
      ]);
      setConexiones(c.data.conexiones || []);
      setOpciones(o.data.pipelines || []);
    } catch (e) { toast.error("No se pudieron cargar las conexiones"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const regenerar = async (id) => {
    try { await axios.post(`${API}/crm/conexiones/${id}/regenerar-token`, {}, getAuthHeaders()); toast.success("Token regenerado"); load(); }
    catch (e) { toast.error("No se pudo regenerar"); }
  };
  const eliminar = async (id) => {
    try { await axios.delete(`${API}/crm/conexiones/${id}`, getAuthHeaders()); toast.success("Conexión eliminada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("URL copiada"); };

  if (!isAdmin) return <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-muted-foreground">Solo administradores pueden gestionar conexiones.</CardContent></Card>;

  return (
    <div className="space-y-4" data-testid="crm-conexiones-tab">
      <div className="flex justify-end">
        <Button data-testid="crm-new-conexion" onClick={() => { setEditId(null); setEditorOpen(true); }} className="gap-1.5"><Plus className="w-4 h-4" /> Nueva conexión</Button>
      </div>
      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div> :
        conexiones.length === 0 ? <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-muted-foreground">No hay conexiones. Crea la primera.</CardContent></Card> :
        <div className="grid gap-3">
          {conexiones.map((c, i) => (
            <Card key={c.id} data-testid={`conexion-card-${i}`} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <Plug className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-slate-800">{c.nombre}</span>
                      <Badge variant="outline" className={c.activo ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-xs" : "bg-slate-100 text-slate-500 text-xs"}>{c.activo ? "Activa" : "Inactiva"}</Badge>
                      {c.modo_api_habilitado && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs gap-1"><DownloadCloud className="w-3 h-3" />API</Badge>}
                      <Badge variant="outline" className="text-xs text-muted-foreground">{c.registros_count} registros</Badge>
                    </div>
                    {c.descripcion && <p className="text-xs text-muted-foreground mt-1">{c.descripcion}</p>}
                    <div className="flex items-center gap-1 mt-2">
                      <Input readOnly value={c.webhook_url} className="font-mono text-[11px] h-8" />
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => copy(c.webhook_url)}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button data-testid={`conx-edit-${i}`} variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setEditId(c.id); setEditorOpen(true); }}>Editar</Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="h-8 text-xs gap-1"><RefreshCw className="w-3.5 h-3.5" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Regenerar token</AlertDialogTitle><AlertDialogDescription>Se invalidará la URL actual de esta conexión.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => regenerar(c.id)}>Regenerar</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button data-testid={`conx-delete-${i}`} variant="outline" size="sm" className="h-8 text-xs text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Eliminar conexión</AlertDialogTitle><AlertDialogDescription>Se elimina la conexión "{c.nombre}". Los registros recibidos se conservan.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => eliminar(c.id)}>Eliminar</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>}
      <ConexionEditor open={editorOpen} conexionId={editId} opciones={opciones} getAuthHeaders={getAuthHeaders}
        onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}

// ---------- Tab: Datos ----------
function DatosTab({ getAuthHeaders, isAdmin }) {
  const navigate = useNavigate();
  const [registros, setRegistros] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_tipo: {} });
  const [conexiones, setConexiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [conexionFilter, setConexionFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [detalle, setDetalle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [distId, setDistId] = useState(null);
  const pageSize = 30;

  const fetchStats = useCallback(async () => {
    try { const res = await axios.get(`${API}/crm/stats${conexionFilter !== "all" ? `?conexion_id=${conexionFilter}` : ""}`, getAuthHeaders()); setStats(res.data); } catch (_) {}
  }, [getAuthHeaders, conexionFilter]);

  const fetchConexiones = useCallback(async () => {
    try { const res = await axios.get(`${API}/crm/conexiones`, getAuthHeaders()); setConexiones(res.data.conexiones || []); } catch (_) {}
  }, [getAuthHeaders]);

  const fetchRegistros = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page); params.append("limit", pageSize);
      if (tipoFilter !== "all") params.append("tipo", tipoFilter);
      if (conexionFilter !== "all") params.append("conexion_id", conexionFilter);
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/crm/registros?${params}`, getAuthHeaders());
      setRegistros(res.data.registros || []); setTotal(res.data.total || 0);
    } catch (err) { toast.error("Error al cargar los registros del CRM"); }
    finally { setLoading(false); }
  }, [page, tipoFilter, conexionFilter, search, getAuthHeaders]);

  useEffect(() => { fetchRegistros(); }, [fetchRegistros]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (isAdmin) fetchConexiones(); }, [isAdmin, fetchConexiones]);

  const generarDemo = async () => {
    try { setBusy(true); await axios.post(`${API}/crm/registros/demo`, {}, getAuthHeaders()); toast.success("Datos de ejemplo generados"); setPage(0); fetchRegistros(); fetchStats(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudieron generar"); } finally { setBusy(false); }
  };
  const limpiarDemo = async () => {
    try { setBusy(true); const res = await axios.delete(`${API}/crm/registros?solo_demo=true`, getAuthHeaders()); toast.success(`${res.data.eliminados} de ejemplo eliminados`); fetchRegistros(); fetchStats(); }
    catch (err) { toast.error("No se pudieron eliminar"); } finally { setBusy(false); }
  };
  const eliminar = async (id) => {
    try { await axios.delete(`${API}/crm/registros/${id}`, getAuthHeaders()); toast.success("Registro eliminado"); fetchRegistros(); fetchStats(); }
    catch (err) { toast.error("No se pudo eliminar"); }
  };
  const distribuir = async (reg) => {
    try { setDistId(reg.id); const res = await axios.post(`${API}/crm/registros/${reg.id}/distribuir`, {}, getAuthHeaders()); toast.success(`Enviado a ${res.data.label}`); fetchRegistros(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo distribuir"); } finally { setDistId(null); }
  };
  const distribuirPendientes = async () => {
    try {
      setBusy(true);
      const q = conexionFilter !== "all" ? `?conexion_id=${conexionFilter}` : "";
      const res = await axios.post(`${API}/crm/registros/distribuir-pendientes${q}`, {}, getAuthHeaders());
      const errs = res.data.errores?.length || 0;
      toast.success(`${res.data.distribuidos} distribuidos${errs ? `, ${errs} con error` : ""}`);
      if (errs) toast.warning(res.data.errores[0].error, { duration: 7000 });
      fetchRegistros();
    } catch (err) { toast.error("No se pudieron distribuir"); } finally { setBusy(false); }
  };

  const formatDate = (iso) => { if (!iso) return "-"; const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6" data-testid="crm-datos-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Total" value={stats.total || 0} icon={Cloud} color="bg-slate-100 text-slate-600" active={tipoFilter === "all"} onClick={() => { setTipoFilter("all"); setPage(0); }} />
        {["renovaciones", "reclamos_salud", "reclamos_riesgos", "clientes", "polizas"].map((t) => { const cfg = TIPO_CONFIG[t]; return <StatCard key={t} label={cfg.label} value={stats.by_tipo?.[t] || 0} icon={cfg.icon} color={cfg.color} active={tipoFilter === t} onClick={() => { setTipoFilter(t); setPage(0); }} />; })}
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="crm-search" placeholder="Buscar por cliente, título o ID..." className="pl-9" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
            </div>
            {isAdmin && (
              <Select value={conexionFilter} onValueChange={(v) => { setConexionFilter(v); setPage(0); }}>
                <SelectTrigger data-testid="crm-conexion-filter" className="w-[180px]"><SelectValue placeholder="Conexión" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todas las conexiones</SelectItem>{conexiones.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={tipoFilter} onValueChange={(v) => { setTipoFilter(v); setPage(0); }}>
              <SelectTrigger data-testid="crm-tipo-filter" className="w-[180px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los tipos</SelectItem>{Object.entries(TIPO_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="crm-refresh" variant="outline" size="sm" onClick={() => { fetchRegistros(); fetchStats(); }} disabled={loading}><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualizar</Button>
            {isAdmin && (<>
              <Button data-testid="crm-distribute-all" variant="outline" size="sm" className="gap-1.5 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" onClick={distribuirPendientes} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Distribuir pendientes</Button>
              <Button data-testid="crm-generate-demo" variant="outline" size="sm" className="gap-1.5 border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100" onClick={generarDemo} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Datos de ejemplo</Button>
              <Button data-testid="crm-clear-demo" variant="outline" size="sm" className="gap-1.5 text-slate-600" onClick={limpiarDemo} disabled={busy}><Trash2 className="w-4 h-4" /> Limpiar ejemplos</Button>
            </>)}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="crm-registros-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left p-3 font-medium text-slate-600">Recibido</th>
                <th className="text-left p-3 font-medium text-slate-600">Conexión</th>
                <th className="text-left p-3 font-medium text-slate-600">Tipo</th>
                <th className="text-left p-3 font-medium text-slate-600">Cliente</th>
                <th className="text-left p-3 font-medium text-slate-600">Detalle</th>
                <th className="text-right p-3 font-medium text-slate-600">Monto</th>
                <th className="text-left p-3 font-medium text-slate-600">Módulo</th>
                <th className="text-center p-3 font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (<tr><td colSpan={8} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></td></tr>)
              : registros.length === 0 ? (<tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No hay datos recibidos aún.</td></tr>)
              : registros.map((r, i) => {
                const cfg = TIPO_CONFIG[r.tipo] || TIPO_CONFIG.otro; const Icon = cfg.icon; const ingestable = INGESTABLES.includes(r.tipo);
                return (
                  <tr key={r.id || i} data-testid={`crm-row-${i}`} className="border-b last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="p-3 whitespace-nowrap text-slate-600 text-xs">{formatDate(r.recibido_at)}</td>
                    <td className="p-3 text-slate-600 text-xs">{r.conexion_nombre || "-"}</td>
                    <td className="p-3"><Badge variant="outline" className={`${cfg.color} gap-1 text-xs`}><Icon className="w-3 h-3" />{cfg.label}</Badge></td>
                    <td className="p-3 font-medium text-slate-700">{r.cliente || "-"}</td>
                    <td className="p-3 max-w-[220px]"><span className="truncate block text-slate-600" title={r.titulo}>{r.titulo || "-"}</span>{r.external_id && <span className="text-xs text-muted-foreground">#{r.external_id}</span>}</td>
                    <td className="p-3 text-right text-slate-700 whitespace-nowrap">{r.monto || "-"}</td>
                    <td className="p-3">
                      {r.distribuido ? (
                        <button data-testid={`crm-open-module-${i}`} onClick={() => navigate(r.distribuido_ruta || "/")} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline" title={`Ver en ${r.distribuido_label}`}><ExternalLink className="w-3.5 h-3.5" /> {r.distribuido_label}</button>
                      ) : ingestable ? <span className="text-xs text-amber-600">Pendiente</span> : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        {isAdmin && ingestable && !r.distribuido && (<Button data-testid={`crm-distribute-${i}`} variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Enviar a su módulo" disabled={distId === r.id} onClick={() => distribuir(r)}>{distId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>)}
                        <Button data-testid={`crm-view-${i}`} variant="ghost" size="icon" className="h-8 w-8" title="Ver JSON" onClick={() => setDetalle(r)}><Eye className="w-4 h-4" /></Button>
                        {isAdmin && (<AlertDialog>
                          <AlertDialogTrigger asChild><Button data-testid={`crm-delete-${i}`} variant="ghost" size="icon" className="h-8 w-8 text-red-500"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar registro</AlertDialogTitle><AlertDialogDescription>No se puede deshacer.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => eliminar(r.id)}>Eliminar</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                        </AlertDialog>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t bg-slate-50/50">
            <p className="text-xs text-muted-foreground">Mostrando {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} de {total}</p>
            <div className="flex items-center gap-1">
              <Button data-testid="crm-prev-page" variant="ghost" size="icon" className="h-8 w-8" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs px-2 text-muted-foreground">{page + 1} / {totalPages}</span>
              <Button data-testid="crm-next-page" variant="ghost" size="icon" className="h-8 w-8" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-w-2xl" data-testid="crm-detail-dialog">
          <DialogHeader><DialogTitle>Datos recibidos del CRM</DialogTitle><DialogDescription>{detalle?.cliente} — {TIPO_CONFIG[detalle?.tipo]?.label || detalle?.tipo}</DialogDescription></DialogHeader>
          <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-auto max-h-[60vh]">{detalle ? JSON.stringify(detalle.payload, null, 2) : ""}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CrmIntegracionPage({ tab = "datos" }) {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  const esConexiones = tab === "configuracion";
  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 overflow-auto" data-testid="crm-integracion-page">
        <div className="max-w-[1400px] mx-auto p-6 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Cloud className="w-6 h-6 text-blue-600" />{esConexiones ? "Conexiones CRM" : "Datos del CRM Externo"}</h1>
            <p className="text-sm text-muted-foreground mt-1">{esConexiones ? "Conecta varias plataformas: cada una con su webhook, enrutamiento y mapeo de campos." : "Información recibida desde tus CRMs externos. Distribúyela a su módulo con un clic."}</p>
          </div>
          {esConexiones ? <ConexionesTab getAuthHeaders={getAuthHeaders} isAdmin={isAdmin} /> : <DatosTab getAuthHeaders={getAuthHeaders} isAdmin={isAdmin} />}
        </div>
      </div>
    </div>
  );
}
